import { Competition, CompetitionSettings, ICompetition, ICompetitionSettings, CompetitionStatus } from '@cyber-range/cyber-range-api-ctf-competition-client';
import { Challenge, IChallenge, ChallengeArtifactType } from '@cyber-range/cyber-range-api-ctf-challenge-client';
import { Team, ITeam } from '@cyber-range/cyber-range-api-ctf-team-client';
import { Player, IPlayer, PlayerStatus } from '@cyber-range/cyber-range-api-ctf-player-client';
import { ITeamSubmissionStatistics, IPlayerSubmissionStatistics, ISubmission, SubmissionStatus, SubmissionType, IChallengeSubmissionStatistics } from '@cyber-range/cyber-range-api-ctf-submission-client';

// --- Competition ---

export const fixtureCompetition: ICompetition = new Competition(<Partial<ICompetition>>{
	id: 'preview-ctf-id',
	name: 'Theme Preview Competition',
	subdomain: 'theme-editor',
	organizationId: 'preview-org-id',
	createdTimestamp: '2025-01-01T00:00:00.000Z',
	modifiedTimestamp: '2025-01-01T00:00:00.000Z',
    recipeParameters: [],
	categories: [
		{ name: 'example challenge colors', index: 0 },
		{ name: 'cryptography', index: 1 },
		{ name: 'forensics', index: 2 },
		{ name: 'reverse engineering', index: 3 },
		{ name: 'web exploitation', index: 4 },
	] as any,
	settings: new CompetitionSettings(<Partial<ICompetitionSettings>>{
		startTime: '2025-01-01T00:00:00.000Z',
		endTime: '2099-12-31T23:59:59.000Z',
		themeId: 'default',
		allowPublicRegistration: true,
		allowTeamNameChange: true,
		allowPlayerNameChange: true,
		allowTeamMembershipChange: true,
		allowUnregisteredUsersToViewDashboard: false,
		hideDashboard: false,
	}),
	status: CompetitionStatus.Ready,
});

// --- Teams ---

export const fixtureTeams: ITeam[] = [
	new Team({ id: 'team-1', competitionId: 'preview-ctf-id', organizationId: 'preview-org-id', name: 'Cyber Phantoms', hidden: false, leaderUserId: 'user-1', createdTimestamp: '2025-01-02T00:00:00.000Z', modifiedTimestamp: '2025-01-02T00:00:00.000Z' }),
	new Team({ id: 'team-2', competitionId: 'preview-ctf-id', organizationId: 'preview-org-id', name: 'Binary Bandits', hidden: false, leaderUserId: 'user-2', createdTimestamp: '2025-01-02T01:00:00.000Z', modifiedTimestamp: '2025-01-02T01:00:00.000Z' }),
	new Team({ id: 'team-3', competitionId: 'preview-ctf-id', organizationId: 'preview-org-id', name: 'Packet Storm', hidden: false, leaderUserId: 'user-3', createdTimestamp: '2025-01-02T02:00:00.000Z', modifiedTimestamp: '2025-01-02T02:00:00.000Z' }),
	new Team({ id: 'team-4', competitionId: 'preview-ctf-id', organizationId: 'preview-org-id', name: 'Stack Overflow', hidden: false, leaderUserId: 'user-4', createdTimestamp: '2025-01-02T03:00:00.000Z', modifiedTimestamp: '2025-01-02T03:00:00.000Z' }),
	new Team({ id: 'team-5', competitionId: 'preview-ctf-id', organizationId: 'preview-org-id', name: 'Zero Day Squad', hidden: false, leaderUserId: 'user-5', createdTimestamp: '2025-01-02T04:00:00.000Z', modifiedTimestamp: '2025-01-02T04:00:00.000Z' }),
];

// --- Player (the current designer user) ---

export const fixturePlayer: IPlayer = new Player({
	id: 'player-1',
	competitionId: 'preview-ctf-id',
	userId: 'preview-user-id',
	name: 'Theme Designer',
	email: 'designer@vacr.io',
	teamId: 'team-1',
	tags: [],
	status: PlayerStatus.Ready,
	createdTimestamp: '2025-01-02T00:00:00.000Z',
	modifiedTimestamp: '2025-01-02T00:00:00.000Z',
});

// --- Challenges (covering all visual states) ---

export const fixtureChallenges: IChallenge[] = [
	// Example Challenge Colors - one per visual state
	new Challenge(<Partial<IChallenge>>{
		id: 'ch-example-solved',
		competitionId: 'preview-ctf-id',
		name: 'Solved Example',
		description: 'This challenge demonstrates the solved state color.',
		points: 100,
		category: 'Example Challenge Colors',
		enabled: true,
		tags: [],
		hints: [],
		flags: [],
		artifacts: [],
		unlocks: [],
		unlockedBy: [],
	}),
	new Challenge(<Partial<IChallenge>>{
		id: 'ch-example-unsolved',
		competitionId: 'preview-ctf-id',
		name: 'Unsolved Example',
		description: 'This challenge demonstrates the unsolved state color.',
		points: 200,
		category: 'Example Challenge Colors',
		enabled: true,
		tags: [],
		hints: [],
		flags: [],
		artifacts: [],
		unlocks: [],
		unlockedBy: [],
	}),
	new Challenge(<Partial<IChallenge>>{
		id: 'ch-example-locked',
		competitionId: 'preview-ctf-id',
		name: 'Locked Example',
		description: 'This challenge demonstrates the locked state color.',
		points: 300,
		category: 'Example Challenge Colors',
		enabled: true,
		tags: [],
		hints: [],
		flags: [],
		artifacts: [],
		unlocks: [],
		unlockedBy: ['ch-example-unsolved'],
	}),
	new Challenge(<Partial<IChallenge>>{
		id: 'ch-example-no-more-attempts',
		competitionId: 'preview-ctf-id',
		name: 'No More Attempts Example',
		description: 'This challenge demonstrates the no more attempts state color.',
		points: 400,
		category: 'Example Challenge Colors',
		enabled: true,
		tags: [],
		hints: [],
		flags: [],
		artifacts: [],
		settings: { maxNumberOfAttempts: 3 },
		unlocks: [],
		unlockedBy: [],
	}),
	new Challenge(<Partial<IChallenge>>{
		id: 'ch-example-disabled',
		competitionId: 'preview-ctf-id',
		name: 'Disabled Example',
		description: 'This challenge demonstrates the disabled state color.',
		points: 500,
		category: 'Example Challenge Colors',
		enabled: false,
		tags: [],
		hints: [],
		flags: [],
		artifacts: [],
		unlocks: [],
		unlockedBy: [],
	}),
	new Challenge(<Partial<IChallenge>>{
		id: 'ch-example-artifacts',
		competitionId: 'preview-ctf-id',
		name: 'Link, File & Environment Artifacts',
		description: 'This challenge demonstrates link, file, and environment artifact types.',
		points: 150,
		category: 'Example Challenge Colors',
		enabled: true,
		tags: ['artifacts'],
		hints: [],
		flags: [],
		artifacts: [
			{ name: 'Challenge Guide', type: ChallengeArtifactType.Link, value: 'https://example.com/guide', sizeInMb: 0, environmentId: '', display: false },
			{ name: 'evidence.pcap', type: ChallengeArtifactType.File, value: 'https://example.com/files/evidence.pcap', sizeInMb: 2.5, environmentId: '', display: false },
			{ name: 'Lab Environment', type: ChallengeArtifactType.Environment, value: '', sizeInMb: 0, environmentId: 'env-preview-1', display: false },
		],
		unlocks: [],
		unlockedBy: [],
	}),
	new Challenge(<Partial<IChallenge>>{
		id: 'ch-example-bot-image',
		competitionId: 'preview-ctf-id',
		name: 'Bot & Image Artifacts',
		description: 'This challenge demonstrates bot and image artifact types.',
		points: 175,
		category: 'Example Challenge Colors',
		enabled: true,
		tags: ['bot', 'image'],
		hints: [],
		flags: [],
		artifacts: [
			{ name: 'AI Assistant', type: ChallengeArtifactType.Bot, value: 'You are a helpful CTF assistant.', sizeInMb: 0, environmentId: '', display: false },
			{ name: 'Network Diagram', type: ChallengeArtifactType.File, value: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0MDAiIGhlaWdodD0iMjAwIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iIzJkMmQ0NCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBkb21pbmFudC1iYXNlbGluZT0ibWlkZGxlIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmaWxsPSIjYWFhIiBmb250LWZhbWlseT0ic2Fucy1zZXJpZiIgZm9udC1zaXplPSIxOCI+TmV0d29yayBEaWFncmFtIFBsYWNlaG9sZGVyPC90ZXh0Pjwvc3ZnPg==', sizeInMb: 0.5, environmentId: '', display: true },
		],
		unlocks: [],
		unlockedBy: [],
	}),

	// Web Exploitation - solved
	new Challenge(<Partial<IChallenge>>{
		id: 'ch-web-1',
		competitionId: 'preview-ctf-id',
		name: 'SQL Injection Basics',
		description: 'Find the SQL injection vulnerability in the login form.',
		points: 100,
		category: 'Web Exploitation',
		enabled: true,
		tags: ['sql', 'injection'],
		hints: [],
		flags: [],
		artifacts: [],
		unlocks: ['ch-web-2'],
		unlockedBy: [],
	}),
	// Web Exploitation - unsolved (unlocked by solving ch-web-1)
	new Challenge(<Partial<IChallenge>>{
		id: 'ch-web-2',
		competitionId: 'preview-ctf-id',
		name: 'XSS Reflected Attack',
		description: 'Exploit the reflected XSS vulnerability to steal admin cookies.',
		points: 200,
		category: 'Web Exploitation',
		enabled: true,
		tags: ['xss', 'reflected'],
		hints: [],
		flags: [],
		artifacts: [],
		unlocks: [],
		unlockedBy: ['ch-web-1'],
	}),
	// Web Exploitation - locked (unlockedBy challenge not solved)
	new Challenge(<Partial<IChallenge>>{
		id: 'ch-web-3',
		competitionId: 'preview-ctf-id',
		name: 'Server-Side Template Injection',
		description: 'Exploit the SSTI vulnerability in the template engine.',
		points: 400,
		category: 'Web Exploitation',
		enabled: true,
		tags: ['ssti'],
		hints: [],
		flags: [],
		artifacts: [],
		unlocks: [],
		unlockedBy: ['ch-web-2'],
	}),

	// Cryptography - unsolved
	new Challenge(<Partial<IChallenge>>{
		id: 'ch-crypto-1',
		competitionId: 'preview-ctf-id',
		name: 'Caesar Cipher',
		description: 'Decrypt the message encoded with a Caesar cipher.',
		points: 50,
		category: 'Cryptography',
		enabled: true,
		tags: ['crypto', 'classical'],
		hints: [],
		flags: [],
		artifacts: [],
		unlocks: [],
		unlockedBy: [],
	}),
	// Cryptography - solved
	new Challenge(<Partial<IChallenge>>{
		id: 'ch-crypto-2',
		competitionId: 'preview-ctf-id',
		name: 'RSA Key Recovery',
		description: 'Recover the private key from the weak RSA implementation.',
		points: 300,
		category: 'Cryptography',
		enabled: true,
		tags: ['rsa', 'crypto'],
		hints: [],
		flags: [],
		artifacts: [],
		unlocks: [],
		unlockedBy: [],
	}),

	// Forensics - noMoreAttempts (simulated via maxAttempts)
	new Challenge(<Partial<IChallenge>>{
		id: 'ch-forensics-1',
		competitionId: 'preview-ctf-id',
		name: 'Memory Dump Analysis',
		description: 'Analyze the memory dump to find the hidden flag.',
		points: 250,
		category: 'Forensics',
		enabled: true,
		tags: ['memory', 'forensics'],
		hints: [],
		flags: [],
		artifacts: [],
		settings: { maxNumberOfAttempts: 3 },
		unlocks: [],
		unlockedBy: [],
	}),
	// Forensics - unsolved
	new Challenge(<Partial<IChallenge>>{
		id: 'ch-forensics-2',
		competitionId: 'preview-ctf-id',
		name: 'Packet Capture Mystery',
		description: 'Examine the pcap file to extract credentials.',
		points: 150,
		category: 'Forensics',
		enabled: true,
		tags: ['pcap', 'network'],
		hints: [],
		flags: [],
		artifacts: [],
		unlocks: [],
		unlockedBy: [],
	}),

	// Reverse Engineering - disabled
	new Challenge(<Partial<IChallenge>>{
		id: 'ch-re-1',
		competitionId: 'preview-ctf-id',
		name: 'Binary Exploitation 101',
		description: 'Reverse engineer the binary to find the hidden flag.',
		points: 500,
		category: 'Reverse Engineering',
		enabled: false,
		tags: ['binary', 'reverse'],
		hints: [],
		flags: [],
		artifacts: [],
		unlocks: [],
		unlockedBy: [],
	}),
	// Reverse Engineering - unsolved
	new Challenge(<Partial<IChallenge>>{
		id: 'ch-re-2',
		competitionId: 'preview-ctf-id',
		name: 'Obfuscated JavaScript',
		description: 'Deobfuscate the JavaScript code to reveal the flag.',
		points: 200,
		category: 'Reverse Engineering',
		enabled: true,
		tags: ['javascript', 'obfuscation'],
		hints: [],
		flags: [],
		artifacts: [],
		unlocks: [],
		unlockedBy: [],
	}),
];

// --- Challenge IDs that the current player's team has solved ---

export const fixtureSolvedChallengeIds: string[] = ['ch-web-1', 'ch-crypto-2', 'ch-example-solved'];

// --- Challenge IDs with no more attempts ---

export const fixtureNoMoreAttemptsChallengeIds: string[] = ['ch-forensics-1', 'ch-example-no-more-attempts'];

// --- Team Submission Statistics (scoreboard) ---

export const fixtureTeamStatistics: ITeamSubmissionStatistics[] = [
	{ teamId: 'team-1', teamName: 'Cyber Phantoms', score: 400, successfulAttempts: 4, failedAttempts: 2, hintsAcquired: 1, scoreModifiedTimestamp: '2025-01-03T10:00:00.000Z' },
	{ teamId: 'team-2', teamName: 'Binary Bandits', score: 350, successfulAttempts: 3, failedAttempts: 5, hintsAcquired: 2, scoreModifiedTimestamp: '2025-01-03T11:00:00.000Z' },
	{ teamId: 'team-3', teamName: 'Packet Storm', score: 250, successfulAttempts: 2, failedAttempts: 3, hintsAcquired: 0, scoreModifiedTimestamp: '2025-01-03T09:30:00.000Z' },
	{ teamId: 'team-4', teamName: 'Stack Overflow', score: 150, successfulAttempts: 1, failedAttempts: 6, hintsAcquired: 3, scoreModifiedTimestamp: '2025-01-03T12:00:00.000Z' },
	{ teamId: 'team-5', teamName: 'Zero Day Squad', score: 100, successfulAttempts: 1, failedAttempts: 1, hintsAcquired: 0, scoreModifiedTimestamp: '2025-01-03T08:00:00.000Z' },
];

// --- Player Submission Statistics ---

export const fixturePlayerStatistics: IPlayerSubmissionStatistics[] = [
	{ playerId: 'player-1', playerName: 'Theme Designer', score: 400, successfulAttempts: 4, failedAttempts: 2, hintsAcquired: 1, scoreModifiedTimestamp: '2025-01-03T10:00:00.000Z', tokenUsage: undefined },
	{ playerId: 'player-2', playerName: 'Alice Hacker', score: 350, successfulAttempts: 3, failedAttempts: 5, hintsAcquired: 2, scoreModifiedTimestamp: '2025-01-03T11:00:00.000Z', tokenUsage: undefined },
	{ playerId: 'player-3', playerName: 'Bob Exploit', score: 250, successfulAttempts: 2, failedAttempts: 3, hintsAcquired: 0, scoreModifiedTimestamp: '2025-01-03T09:30:00.000Z', tokenUsage: undefined },
	{ playerId: 'player-4', playerName: 'Charlie Root', score: 150, successfulAttempts: 1, failedAttempts: 6, hintsAcquired: 3, scoreModifiedTimestamp: '2025-01-03T12:00:00.000Z', tokenUsage: undefined },
	{ playerId: 'player-5', playerName: 'Dana Shell', score: 100, successfulAttempts: 1, failedAttempts: 1, hintsAcquired: 0, scoreModifiedTimestamp: '2025-01-03T08:00:00.000Z', tokenUsage: undefined },
];

// --- Submissions (marks solved challenges for the current player's team) ---

export const fixtureSubmissions: ISubmission[] = fixtureSolvedChallengeIds.map((challengeId, index) => (<ISubmission>{
	id: `sub-${index + 1}`,
	organizationId: 'preview-org-id',
	competitionId: 'preview-ctf-id',
	type: SubmissionType.Flag,
	challengeId,
	challengeName: fixtureChallenges.find(c => c.id === challengeId)?.name,
	challengeCategory: fixtureChallenges.find(c => c.id === challengeId)?.category,
	scoreAdjustment: 100,
	hintId: '',
	playerId: 'player-1',
	teamId: 'team-1',
	status: SubmissionStatus.Correct,
	createdTimestamp: `2025-01-03T0${index}:00:00.000Z`,
	modifiedTimestamp: `2025-01-03T0${index}:00:00.000Z`,
	submittedFlag: '',
	explanation: '',
	tokenUsage: undefined,
}));

// --- My team's per-challenge statistics (drives "no more attempts" state) ---

export const fixtureMyTeamChallengeStatistics: IChallengeSubmissionStatistics[] = fixtureNoMoreAttemptsChallengeIds.map(challengeId => ({
	challengeId,
	successfulAttempts: 0,
	failedAttempts: 3, // matches maxNumberOfAttempts: 3
}));
